import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faFilm, faTv, faMusic, faPodcast, faSearch,
    faBars, faTimes
} from '@fortawesome/free-solid-svg-icons';
import { APP_CONFIG } from '../../config';
import { useDevice } from '../../hooks';

interface HeaderProps {
    onSearch?: (query: string) => void;
    searchValue?: string;
    showSearch?: boolean;
    transparent?: boolean;
    className?: string;
}

const Header: React.FC<HeaderProps> = ({
    onSearch,
    searchValue = '',
    showSearch = true,
    transparent = false,
    className = ''
}) => {
    const navigate = useNavigate();
    const location = useLocation();
    useDevice(); // For future responsive enhancements
    const [isScrolled, setIsScrolled] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [searchFocused, setSearchFocused] = useState(false);

    /** TV / on-screen keyboards often commit text without firing events React listens to; keep controlled state in sync. */
    const flushSearchFromInput = (el: HTMLInputElement) => {
        onSearch?.(el.value);
    };

    const navItems = [
        { path: '/', label: 'Movies', icon: faFilm },
        { path: '/tv', label: 'Live TV', icon: faTv },
        { path: '/music', label: 'Music', icon: faMusic },
        { path: '/podcasts', label: 'Podcasts', icon: faPodcast },
    ];

    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 50);
        };

        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Close mobile menu on route change
    useEffect(() => {
        setMobileMenuOpen(false);
    }, [location.pathname]);

    const isActive = (path: string): boolean => {
        // Exact match for home page
        if (path === '/') {
            return location.pathname === '/' || location.pathname.startsWith('/media') || location.pathname.startsWith('/actor');
        }

        // For /tv, match /tv and /channel routes
        if (path === '/tv') {
            return location.pathname === '/tv' || location.pathname.startsWith('/channel');
        }

        // For other paths, use startsWith
        return location.pathname.startsWith(path);
    };

    const headerClasses = `
    fixed top-0 left-0 right-0 z-50
    transition-all duration-500 ease-out
    ${isScrolled || !transparent
            ? 'bg-black/90 backdrop-blur-xl shadow-2xl shadow-black/50'
            : 'bg-gradient-to-b from-black/80 via-black/40 to-transparent'
        }
    ${className}
  `;

    return (
        <>
            <header className={headerClasses}>
                <div className="max-w-[2000px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-12 2xl:px-16">
                    <div className="flex items-center justify-between h-16 sm:h-18 lg:h-20 xl:h-24 2xl:h-28">

                        {/* Logo */}
                        <button
                            onClick={() => navigate('/')}
                            className="flex items-center gap-2 sm:gap-3 group focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 rounded-lg px-2 py-1"
                        >
                            <div className="relative">
                                <span className="text-xl sm:text-2xl lg:text-3xl xl:text-4xl 2xl:text-5xl font-black tracking-tight">
                                    <span className="bg-gradient-to-r from-amber-400 via-orange-500 to-rose-500 bg-clip-text text-transparent
                    drop-shadow-[0_0_30px_rgba(251,191,36,0.5)]
                    group-hover:drop-shadow-[0_0_40px_rgba(251,191,36,0.8)]
                    transition-all duration-300">
                                        {APP_CONFIG.name}
                                    </span>
                                </span>
                                <div className="absolute -bottom-1 left-0 right-0 h-0.5 bg-gradient-to-r from-amber-400 via-orange-500 to-rose-500 
                  transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 rounded-full" />
                            </div>
                        </button>

                        {/* Desktop Navigation */}
                        <nav className="hidden md:flex items-center gap-1 lg:gap-2 xl:gap-3">
                            {navItems.map((item) => (
                                <button
                                    key={item.path}
                                    onClick={() => navigate(item.path)}
                                    className={`
                    relative px-4 lg:px-5 xl:px-6 2xl:px-8 py-2 lg:py-2.5 xl:py-3 2xl:py-4
                    rounded-full font-semibold
                    text-sm lg:text-base xl:text-lg 2xl:text-xl
                    transition-all duration-300 ease-out
                    focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500
                    ${isActive(item.path)
                                            ? 'bg-gradient-to-r from-amber-500 via-orange-500 to-rose-500 text-white shadow-lg shadow-orange-500/30'
                                            : 'text-gray-300 hover:text-white hover:bg-white/10'
                                        }
                    group
                  `}
                                >
                                    <FontAwesomeIcon
                                        icon={item.icon}
                                        className={`mr-2 transition-transform duration-300 ${isActive(item.path) ? '' : 'group-hover:scale-110'}`}
                                    />
                                    <span>{item.label}</span>

                                    {/* Hover glow effect */}
                                    {!isActive(item.path) && (
                                        <div className="absolute inset-0 rounded-full bg-gradient-to-r from-amber-500/0 via-orange-500/0 to-rose-500/0 
                      group-hover:from-amber-500/20 group-hover:via-orange-500/20 group-hover:to-rose-500/20
                      transition-all duration-300 -z-10" />
                                    )}
                                </button>
                            ))}
                        </nav>

                        {/* Search & Mobile Menu */}
                        <div className="flex items-center gap-3 sm:gap-4">

                            {/* Search Bar */}
                            {showSearch && (
                                <form
                                    className={`
                  relative flex items-center
                  transition-all duration-300 ease-out
                  ${searchFocused ? 'w-48 sm:w-64 lg:w-80 xl:w-96' : 'w-40 sm:w-48 lg:w-64'}
                `}
                                    onSubmit={(e) => {
                                        e.preventDefault();
                                        const el = e.currentTarget.elements.namedItem('header-search') as HTMLInputElement | null;
                                        if (el) flushSearchFromInput(el);
                                    }}
                                >
                                    <input
                                        name="header-search"
                                        type="search"
                                        enterKeyHint="search"
                                        value={searchValue}
                                        onChange={(e) => onSearch?.(e.target.value)}
                                        onCompositionEnd={(e) => flushSearchFromInput(e.currentTarget)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' || e.key === 'NumpadEnter') {
                                                flushSearchFromInput(e.currentTarget);
                                            }
                                        }}
                                        onFocus={() => setSearchFocused(true)}
                                        onBlur={(e) => {
                                            flushSearchFromInput(e.currentTarget);
                                            setSearchFocused(false);
                                        }}
                                        placeholder="Search..."
                                        className={`
                      w-full pl-4 pr-10 py-2 lg:py-2.5 xl:py-3 2xl:py-4
                      text-sm lg:text-base xl:text-lg
                      bg-white/10 backdrop-blur-sm
                      border border-white/20
                      rounded-full
                      text-white placeholder-gray-400
                      focus:outline-none focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500/50
                      focus:bg-white/15
                      transition-all duration-300
                    `}
                                    />
                                    <FontAwesomeIcon
                                        icon={faSearch}
                                        className={`
                      absolute right-4 text-gray-400
                      transition-colors duration-300
                                        ${searchFocused ? 'text-amber-500' : ''}
                    `}
                                    />
                                </form>
                            )}

                            {/* Mobile Menu Button */}
                            <button
                                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                                className="md:hidden p-2 rounded-lg text-gray-300 hover:text-white hover:bg-white/10 
                  transition-all duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500"
                                aria-label="Toggle menu"
                            >
                                <FontAwesomeIcon
                                    icon={mobileMenuOpen ? faTimes : faBars}
                                    className="text-xl"
                                />
                            </button>
                        </div>
                    </div>
                </div>

                {/* Mobile Navigation Menu */}
                <div className={`
          md:hidden
          overflow-hidden
          transition-all duration-300 ease-out
          ${mobileMenuOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}
        `}>
                    <nav className="px-4 pb-6 pt-2 bg-black/95 backdrop-blur-xl border-t border-white/10">
                        <div className="flex flex-col gap-2">
                            {navItems.map((item) => (
                                <button
                                    key={item.path}
                                    onClick={() => navigate(item.path)}
                                    className={`
                    flex items-center gap-3 px-4 py-3
                    rounded-xl font-medium
                    transition-all duration-300
                    ${isActive(item.path)
                                            ? 'bg-gradient-to-r from-amber-500 via-orange-500 to-rose-500 text-white'
                                            : 'text-gray-300 hover:text-white hover:bg-white/10'
                                        }
                  `}
                                >
                                    <FontAwesomeIcon icon={item.icon} className="w-5" />
                                    <span>{item.label}</span>
                                </button>
                            ))}
                        </div>
                    </nav>
                </div>
            </header>

            {/* Spacer for fixed header */}
            <div className="h-16 sm:h-18 lg:h-20 xl:h-24 2xl:h-28" />
        </>
    );
};

export default Header;
